import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { FieldPath, FieldValues, PathValue, UseFormReturn } from 'react-hook-form'

import { getCountriesList, getCountriesListQueryKey } from '../../api/countries/get-countries-list'
import { findCountry } from '../utils/functions/country.functions'

/**
 * Returns a function giving a stored address country in the country's own language
 * (e.g. "Allemagne" → "Deutschland"). Unknown values are returned as they are.
 */
export function useNativeCountryName() {
  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList(),
    staleTime: Infinity
  })

  return useCallback(
    (country?: string | null) => {
      if (!country || !countryList) return country ?? undefined
      return findCountry(country, countryList.data)?.nativeName ?? country
    },
    [countryList]
  )
}

/**
 * Shows the stored country of an address form field in the country's own language once the
 * countries list is loaded, so the address is saved with it. Values typed afterwards are kept as typed.
 */
export function useNativeCountryField<T extends FieldValues>(
  { getValues, setValue }: Pick<UseFormReturn<T>, 'getValues' | 'setValue'>,
  name: FieldPath<T>
) {
  const nativeCountryName = useNativeCountryName()

  useEffect(() => {
    const country = getValues(name)
    const nativeCountry = nativeCountryName(country)
    if (nativeCountry && nativeCountry !== country)
      setValue(name, nativeCountry as PathValue<T, FieldPath<T>>)
  }, [getValues, setValue, name, nativeCountryName])
}
